import type { JugadorEnMesa, MesaPublica } from '../datos/index.js'
import { Bandera } from './Bandera.js'
import { Icono } from './Icono.js'
import { manosAnotadas } from './PlanillaDeMesa.js'

/**
 * Una mesa del salón, mirada desde afuera.
 *
 * Es la hermana pública de `MesaDelJugador`, y la diferencia está en el punto
 * de vista. Aquella se mira desde una de las cuatro sillas y por eso dice
 * «Contigo» y «En contra»; esta es casi siempre la mesa de otros, así que los
 * bandos van sin orientar y lo único que la propia mesa tiene de distinto es
 * que se encuentra sola en una lista de cien.
 *
 * Los puntos van adentro de cada bando y no en un renglón aparte con el «188 a
 * 200» de siempre. Es lo que hace que la tarjeta siga diciendo quién ganó
 * cuando los dos lados se apilan en un teléfono angosto: con el marcador
 * abajo, el número de la izquierda deja de estar a la izquierda de nadie.
 */

function Nombre({ jugador }: { jugador: JugadorEnMesa }) {
  const ausente = jugador.motivo === 'ausencia' || jugador.motivo === 'abandono'

  return (
    <span className={`silla ${jugador.esMio ? 'mia' : ''}`}>
      <Bandera codigo={jugador.pais} />
      {jugador.numero !== null && <span className="silla-numero">{jugador.numero}</span>}
      <span className="silla-nombre acortar">{jugador.nombre}</span>
      {jugador.esMio && <span className="silla-tu">tú</span>}
      {/* La ausencia es de una persona y no del bando: los otros tres ganan y
          solo ella pierde. Si no se dijera acá, la mesa mostraría una derrota
          sin partida y nadie entendería el marcador. */}
      {ausente && (
        <span className="pastilla error">
          {jugador.motivo === 'ausencia' ? 'No se presentó' : 'Abandonó'}
        </span>
      )}
    </span>
  )
}

function Bando({
  jugadores,
  puntos,
  estado,
}: {
  jugadores: JugadorEnMesa[]
  /** Nulo mientras la mesa no se cargó ni tiene manos: no hay nada que mostrar. */
  puntos: number | null
  /** `en-vivo` es el marcador parcial de la carga mano a mano: todavía no es un resultado. */
  estado: 'gana' | 'pierde' | 'sin-cargar' | 'en-vivo'
}) {
  return (
    <div className={`bando ${estado}`}>
      {puntos !== null && <span className="bando-puntos">{puntos}</span>}
      <ul>
        {jugadores.map((j) => (
          <li key={j.participanteId}>
            <Nombre jugador={j} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function MesaEnJuego({
  mesa,
  manosAbiertas = false,
  alVerManos,
  alAnotar,
}: {
  mesa: MesaPublica
  /** Si la planilla mano a mano de esta mesa está desplegada debajo. */
  manosAbiertas?: boolean
  /** Sin esto la tarjeta no ofrece abrir la planilla. */
  alVerManos?: () => void
  /** Abre la libreta de anotación. Solo en la mesa propia y mientras se juega. */
  alAnotar?: () => void
}) {
  const marcador = mesa.marcador
  /* El parcial solo mientras no hay resultado: con la mesa cargada, lo que
     cuenta es el marcador oficial aunque las manos digan otra cosa. */
  const parcial = marcador ? null : mesa.parcial
  const bando = (n: 1 | 2) => mesa.jugadores.filter((j) => j.bando === n)

  const estadoDe = (n: 1 | 2): 'gana' | 'pierde' | 'sin-cargar' | 'en-vivo' => {
    if (parcial) return 'en-vivo'
    if (!marcador || marcador.ganador === null) return 'sin-cargar'
    return marcador.ganador === n ? 'gana' : 'pierde'
  }
  const puntosDe = (n: 1 | 2): number | null => {
    if (marcador) return n === 1 ? marcador.bando1 : marcador.bando2
    if (parcial) return n === 1 ? parcial.bando1 : parcial.bando2
    return null
  }

  return (
    <article className={`mesa-publica ${mesa.esMia ? 'mia' : ''}`}>
      <header className="mesa-publica-cabecera">
        {mesa.tipo === 'incompleta' ? (
          <span className="mesa-numero sin-mesa">Sin mesa</span>
        ) : (
          <span className="mesa-numero">
            Mesa <b>{mesa.numero}</b>
          </span>
        )}
        {mesa.esMia && <span className="silla-tu">tu mesa</span>}
        {mesa.tipo === 'incompleta' ? (
          <span className="pastilla info">Descansan</span>
        ) : mesa.estado === 'cargada' ? (
          <span className="pastilla cerrado">Cargada</span>
        ) : alAnotar ? (
          <button className="pastilla vivo mesa-anotar-pastilla" onClick={alAnotar}>
            Anotar
            <Icono nombre="flecha" tamano={13} />
          </button>
        ) : (
          <span className="pastilla vivo">Jugando</span>
        )}
      </header>

      {mesa.tipo === 'incompleta' ? (
        <ul className="mesa-publica-sueltos">
          {mesa.jugadores.map((j) => (
            <li key={j.participanteId}>
              <Nombre jugador={j} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="mesa-publica-bandos">
          <Bando jugadores={bando(1)} puntos={puntosDe(1)} estado={estadoDe(1)} />
          <div className="lado-separador" aria-hidden="true">
            <span>vs</span>
          </div>
          <Bando jugadores={bando(2)} puntos={puntosDe(2)} estado={estadoDe(2)} />
        </div>
      )}

      {!marcador && mesa.tipo !== 'incompleta' && (
        <footer className="mesa-pendiente">
          {parcial
            ? `En vivo: ${manosAnotadas(parcial.manos)} anotadas, a ${mesa.objetivoPuntos} puntos`
            : `Se juega a ${mesa.objetivoPuntos} puntos`}
        </footer>
      )}

      {alVerManos && mesa.parcial && (
        <button className="mesa-ver-manos" onClick={alVerManos} aria-expanded={manosAbiertas}>
          {manosAbiertas ? 'Ocultar mano a mano' : 'Ver mano a mano'}
        </button>
      )}
    </article>
  )
}
